/**
 * EventManager - Merkezi Event Yönetim Sistemi
 * 
 * Bu sınıf, tüm analitik araçları tek bir noktadan yönetmek için kullanılır.
 * Android'deki EventManager yapısına benzer şekilde çalışır.
 */

import { setSecureStorage, getSecureStorage } from '../security/cookieUtils';
class EventManager {
  constructor() {
    this.providers = [];
    this.initialized = false;
    this.debug = true; // Her zaman debug modunu açık tut
    this.sentEvents = {}; // Gönderilen event'leri takip etmek için
  }

  /**
   * Analiz sağlayıcısını kaydeder
   * @param {Object} provider - Analiz sağlayıcısı
   * @returns {EventManager} - Zincir metodlar için this döndürür
   */
  registerProvider(provider) {
    if (!provider) {
      console.warn('EventManager: Geçersiz provider');
      return this;
    }
    
    this.providers.push(provider);
    
    if (this.debug) {
      console.log(`EventManager: ${provider.name || 'İsimsiz'} provider kaydedildi`);
    }
    
    return this;
  }

  /**
   * Tüm sağlayıcıları başlatır
   * @returns {EventManager} - Zincir metodlar için this döndürür
   */
  initialize() {
    if (this.initialized) {
      console.warn('EventManager: Zaten başlatılmış');
      return this;
    }
    
    this.providers.forEach(provider => {
      try {
        if (provider.initialize) {
          provider.initialize();
          
          if (this.debug) {
            console.log(`EventManager: ${provider.name || 'İsimsiz'} provider başlatıldı`);
          }
        }
      } catch (error) {
        console.error(`EventManager: ${provider.name || 'İsimsiz'} provider başlatılırken hata oluştu`, error);
      }
    });
    
    this.initialized = true;
    
    if (this.debug) {
      console.log('EventManager: Tüm providerlar başlatıldı');
    }
    
    return this;
  }

  /**
   * Session ID oluşturur veya mevcut ID'yi kullanır
   * @returns {string} Session ID
   */
  getOrCreateSessionId() {
    // localStorage'dan session ID'yi al
    let sessionId = getSecureStorage('mona_session_id');
    
    // Eğer session ID yoksa veya geçersizse, yeni bir tane oluştur
    if (!sessionId) {
      sessionId = 'session_' + new Date().getTime() + '_' + Math.random().toString(36).substring(2, 15);
      setSecureStorage('mona_session_id', sessionId);
      
      // Yeni oturum başladığında, gönderilen event'leri sıfırla
      this.sentEvents = {};
      
      if (this.debug) {
        console.log('Yeni oturum başlatıldı:', sessionId);
      }
    }
    
    return sessionId;
  }

  // UTM ve referrer bilgilerini al
  getReferrerAndUTM() {
    const referrer = document.referrer;
    const urlParams = new URLSearchParams(window.location.search);
    
    return {
      utm_source: urlParams.get('utm_source') || null,
      utm_medium: urlParams.get('utm_medium') || null,
      utm_campaign: urlParams.get('utm_campaign') || null,
      utm_term: urlParams.get('utm_term') || null,
      utm_content: urlParams.get('utm_content') || null,
      referrer: referrer || null
    };
  }

  /**
   * Event gönderir
   * @param {string} eventName - Event adı
   * @param {Object} eventParams - Event parametreleri
   * @param {boolean} allowDuplicate - Aynı event'in tekrar gönderilmesine izin ver (varsayılan: false)
   */
  trackEvent(eventName, eventParams = {}, allowDuplicate = false) {
    if (!this.initialized) {
      console.warn('EventManager: Başlatılmadan event gönderilmeye çalışıldı');
      return;
    }
    
    // UTM ve referrer bilgilerini eventParams'a ekle
    const utmAndReferrer = this.getReferrerAndUTM();
    const enrichedParams = {
      ...eventParams,
      ...utmAndReferrer,
      timestamp: new Date().getTime()
    };
    
    // Session ID oluştur veya mevcut ID'yi kullan
    const sessionId = this.getOrCreateSessionId();
    
    // Event'in benzersiz kimliğini oluştur
    const eventKey = `${sessionId}_${eventName}_${JSON.stringify(eventParams)}`;
    
    // Eğer event daha önce gönderilmişse ve tekrara izin verilmiyorsa, gönderme
    if (!allowDuplicate && this.sentEvents[eventKey]) {
      if (this.debug) {
        console.log(`Event engellendi (duplikasyon): ${eventName}`, eventParams);
      }
      return;
    }
    
    // Event'i gönderildi olarak işaretle
    this.sentEvents[eventKey] = true;
    
    this.providers.forEach(provider => {
      try {
        if (provider.trackEvent) {
          provider.trackEvent(eventName, enrichedParams);
        }
      } catch (error) {
        console.error(`EventManager: ${provider.name || 'İsimsiz'} provider event gönderirken hata oluştu`, error);
      }
    });
    
    if (this.debug) {
      console.log(`Event gönderildi: ${eventName}`, enrichedParams);
    }
  }

  /**
   * Sayfa görüntüleme eventi gönderir
   * @param {string} path - Sayfa yolu
   * @param {string} pageTitle - Sayfa başlığı
   */
  trackPageView(path, pageTitle) {
    if (!this.initialized) {
      console.warn('EventManager: Başlatılmadan sayfa görüntüleme eventi gönderilmeye çalışıldı');
      return;
    }
    
    this.providers.forEach(provider => {
      try {
        if (provider.trackPageView) {
          provider.trackPageView(path, pageTitle);
        }
      } catch (error) {
        console.error(`EventManager: ${provider.name || 'İsimsiz'} provider sayfa görüntüleme eventi gönderirken hata oluştu`, error);
      }
    });
    
    if (this.debug) {
      console.log(`PageView: ${path}`, { title: pageTitle });
    }
  }

  /**
   * Kullanıcı kimliği ayarlar
   * @param {string} userId - Kullanıcı kimliği
   */
  setUserId(userId) {
    if (!this.initialized) {
      console.warn('EventManager: Başlatılmadan kullanıcı kimliği ayarlanmaya çalışıldı');
      return;
    }
    
    this.providers.forEach(provider => {
      try {
        if (provider.setUserId) {
          provider.setUserId(userId);
        }
      } catch (error) {
        console.error(`EventManager: ${provider.name || 'İsimsiz'} provider kullanıcı kimliği ayarlarken hata oluştu`, error);
      }
    });
    
    if (this.debug) {
      console.log(`UserId: ${userId}`);
    }
  }

  /**
   * Kullanıcı özelliklerini ayarlar
   * @param {Object} properties - Kullanıcı özellikleri
   */
  setUserProperties(properties) {
    if (!this.initialized) {
      console.warn('EventManager: Başlatılmadan kullanıcı özellikleri ayarlanmaya çalışıldı');
      return;
    }
    
    this.providers.forEach(provider => {
      try {
        if (provider.setUserProperties) {
          provider.setUserProperties(properties);
        }
      } catch (error) {
        console.error(`EventManager: ${provider.name || 'İsimsiz'} provider kullanıcı özellikleri ayarlarken hata oluştu`, error);
      }
    });
    
    if (this.debug) {
      console.log(`UserProperties:`, properties);
    }
  }
}

// Singleton instance
const eventManager = new EventManager();
export default eventManager; 